import * as d3 from 'd3'
import SysUtility from './SysUtility.js'

/**
 * Creates a custom marker component
 * @class
 */
export default class SysMarker{

    #markerContainer
    #ref
    #dataset
    #mergedDataset
    #transitionTime
    #tooltip
    
  
    /**
     * Creates the object
     * @param {Object} params - allowed params: 
     *  ref {Object} - graph object
     *  transitionTime {Number}
     */
    constructor(params){
  
      this.#markerContainer = null;
      this.#ref = params.ref;
      this.#transitionTime = params.transitionTime || 500; 
      this.#dataset = null;  
      this.#mergedDataset = null;  
      this.#tooltip = this.#ref.getTooltip();
  
      this.init();
  
    }
  
    /**
     * Creates an marker container
     */
    init(){
      this.#markerContainer = this.#ref.getSVG()
        .append("g").attr("id", "markerContainer")
        .attr("clip-path", `url(#slc_${this.#ref.getExternalId()}_clip)`);
    }

    /**
     * Toggle marker visibility
     * @param {Boolean} opacity 
     */
    toggleVisibility(opacity){
      this.#markerContainer
      .attr("transform", `scale(${opacity})`)
      .transition().duration(this.#transitionTime).style("opacity", opacity);
    }

    /**
     * Deletes all markers from graph
     */
    clearAll(){
      this.#markerContainer.selectAll("*").transition().duration(this.#transitionTime).style("opacity", 0).remove();
    }

    /**
     * Gets updated data from reference
     */
    updateReferenceData(){
      this.#dataset = this.#ref.getDataset();  
      this.#mergedDataset = this.#ref.getMergedDataset();  
    }    

    /**
     * Draws markers
     */
    drawMarkers(){

      // get updated dataset from reference
      this.updateReferenceData();

      this.drawLines();

      this.drawBottomMarkers();

      this.#markerContainer
      .style("opacity", 0)
      .transition().duration(this.#transitionTime).style("opacity",  this.#ref.getConfigParam("opacity.marker"));
    }

    /**
     * Draws marker lines
     */
    drawLines(){

      // let dataset = this.#ref.getAxisMode() === 3 ? this.#dataset[0].data : this.#mergedDataset;

      this.#markerContainer.selectAll("markerContainer")
        .data(this.#dataset)
        .enter()
        .append("g")
        .selectAll("markerLineContainer")
        .data(d => d.markers)
        .enter()
        .append("line")// attach a line
        .attr("class", "sys-lc-markerLine") // set default color  
        .style("stroke", d => d.color) // colour the line
        //.attr("style", d => "stroke:" + d.color ) // colour the line
        .attr("x1", d => this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) ))     // x position of the first end of the line
        //.attr("y1", this.#ref.getYScaleLeft()( d3.min(dataset, (d) => +d.value ) ))      // y position of the first end of the line
        .attr("y1", this.#ref.getYScaleLeft()( this.#ref.getConfigParam("rescale.left.min") ))
        .attr("x2", d => this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) ))     // x position of the second end of the line
        //.attr("y2", this.#ref.getYScaleLeft()( d3.max(dataset, (d) => +d.value ) ))
        .attr("y2", this.#ref.getYScaleLeft()( this.#ref.getConfigParam("rescale.left.max") ))
    }

    /**
     * Draws marker markers
     */
    drawBottomMarkers(){

      // let dataset = this.#ref.getAxisMode() === 3 ? this.#dataset[0].data : this.#mergedDataset;

      this.#markerContainer .selectAll("markerContainer")
        .data(this.#dataset)
        .enter()
        .append("g")
        .selectAll("markerLineContainer")
        .data(d => d.markers)
        .enter()
        .append('path')
        .attr("class", "sys-lc-markerPoint")
        // .attr("style", d => "stroke:" + d.color ) // colour the line
        // -6px used to translate the base of the trinagle on the x line
        .attr("transform", d => {
          // if data is empty, rescale is empty too
          if(this.#ref.getConfigParam("rescale.left.min") !== null){
            return `translate(${this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) )},
                    ${(this.#ref.getYScaleLeft()( this.#ref.getConfigParam("rescale.left.min"))) - 6})`;
          } else {
            return `translate(-100,-100)`;
          }
        })  
        .attr("d", d3.symbol().type(d3.symbolSquare))
        .attr("data-group", d => d.group )
        .attr("data-date", (d) => SysUtility.formatTime(this.#ref.timeOutFormat, d.date) )
        .attr("data-type", (d) => d.type )
        .on("mouseover", this.#tooltip.pointMouseover)
        .on("mousemove", this.#tooltip.markerPointMousemove)
        .on("mouseleave", this.#tooltip.pointMouseleave)
        .style("stroke", d => d.color)
        .attr("fill", d => d.color)
    }

    /**
     * Updates markers
     */
    updateMarkers(){

      // get updated dataset from reference
      this.updateReferenceData();

      this.updateLines();

      this.updateBottomMarkers();
      
    }

    /**
     * Update markers line
     */
    updateLines(){
      this.#markerContainer.selectAll(".sys-lc-markerLine")
        .transition()
        .duration(this.#transitionTime)
        .attr("x1", d => this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) ))     // x position of the first end of the line
        .attr("x2", d => this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) ))     // x position of the second end of the line
    }

    /**
     * Update markers bottom marker
     */
    updateBottomMarkers(){

      this.#markerContainer.selectAll(".sys-lc-markerPoint")
        .transition()
        .duration(this.#transitionTime)
        //.attr("transform", d => `translate(${this.#ref.getXScale()( SysUtility.parseTime(this.timeInFormat, d.date) )},
        //                                   ${this.#ref.getYScaleLeft()( d3.min(dataset, (d) => +d.value ) )})` )
        .attr("transform", d => `translate(${this.#ref.getXScale()( SysUtility.parseTime(this.#ref.timeInFormat, d.date) )},
                                          ${this.#ref.getYScaleLeft()( this.#ref.getConfigParam("rescale.left.min") )})` )
    }

    /**
     * Update marker border color on marker
     * @param {String} group 
     * @param {String} color 
     */
    updateColor(group, color){
      this.#markerContainer.selectAll(`path[data-group=${group}]`)
        .transition().duration(this.#transitionTime)
        .attr("style", () => "stroke:" + color )
    }

  }
  